// RIBBON row — measurement + priority collapse (R0, 2026-07-20).
//
// The row owns the DOM half of the collapse system; the DECISION half lives in
// collapsePriority.mjs (pure, unit-tested). Mechanism:
//
//   1. A hidden MEASURE LAYER renders every group twice — expanded, and as its
//      collapsed chip — absolutely positioned, visibility:hidden, aria-hidden.
//      A layout effect reads natural widths after every render, so widths
//      track state changes (a longer font name, touch density) for free and
//      the visible row NEVER feeds back into its own measurement (no
//      ResizeObserver loops — the classic priority-ribbon trap).
//   2. A ResizeObserver on the row reports available width.
//   3. computeCollapse(availableWidth, widths, priorities) → visible/collapsed
//      sets; collapsed groups render as labeled "…" chips IN PLACE (slots and
//      gap count constant — the algorithm's core assumption).
//
// data-measured / data-fits / data-collapsed-ids are probe hooks (the lab's
// Playwright suite asserts sacrifice order through them).
//
// SECTION DIVIDERS (R3, owner: "sections … labelled and separated with a |"):
// rowset.dividers renders a real | rule element between every pair of group
// slots (data-dividers marks the row; .rbn-sec-rule is the element, so probes
// can census them). The rule before the tail slot carries the margin-auto
// push (CSS) so the divider hugs POSITION at the right edge. Collapse math
// stays honest: each boundary now costs gap + rule(1px) + gap, so the row
// passes computeCollapse an EFFECTIVE gap of (2·ROW_GAP + 1) — the algorithm
// still assumes constant slot/gap counts, which dividers preserve (chips keep
// their rules).
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { computeCollapse, ROW_GAP } from './collapsePriority.mjs';
import RibbonGroup from './RibbonGroup';

export default function RibbonRow({
  rowId,
  rowset,
  state = {},
  handlers = {},
  onOpenDrawer,
  disabledGroupIds = new Set(),
}) {
  // showWhen (R2): selection-conditional groups (Ungroup, smart-object Edit,
  // DPI badge) render only while their mirror is truthy — filtered BEFORE
  // measurement and collapse so hidden groups cost no width and never enter
  // the sacrifice order.
  const groups = (rowset?.groups || []).filter((g) => !g.showWhen || !!state[g.showWhen]);
  const dividers = !!rowset?.dividers;
  const rowRef = useRef(null);
  const measureRef = useRef(null);
  const [availWidth, setAvailWidth] = useState(0);
  const [widths, setWidths] = useState({});

  // -- available width (ResizeObserver on the visible row) --
  useLayoutEffect(() => {
    const el = rowRef.current;
    if (!el) return undefined;
    const read = () => setAvailWidth(Math.floor(el.clientWidth));
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // -- natural widths (measure layer, after every render) --
  useLayoutEffect(() => {
    const layer = measureRef.current;
    if (!layer) return;
    const next = {};
    layer.querySelectorAll('[data-measure-id]').forEach((n) => {
      next[n.getAttribute('data-measure-id')] = Math.ceil(n.getBoundingClientRect().width);
    });
    setWidths((prev) => {
      const keys = Object.keys(next);
      if (keys.length === Object.keys(prev).length && keys.every((k) => prev[k] === next[k])) {
        return prev; // unchanged — no re-render loop
      }
      return next;
    });
  });

  const measured = groups.every((g) => Number.isFinite(widths[g.id]));

  const result = useMemo(() => computeCollapse({
    groups: groups.map((g) => ({
      id: g.id,
      width: widths[g.id],
      chipWidth: widths[`chip:${g.id}`],
      collapsePriority: g.collapsePriority,
      neverCollapse: g.neverCollapse,
    })),
    availableWidth: availWidth,
    // dividered rows: each boundary = gap + 1px rule + gap (see header)
    gap: dividers ? ROW_GAP * 2 + 1 : ROW_GAP,
  }), [groups, widths, availWidth, dividers]);

  const collapsedSet = useMemo(() => new Set(result.collapsed), [result]);

  return (
    <div
      className="rbn-row"
      data-row-id={rowId}
      data-dividers={dividers ? 'true' : 'false'}
      data-measured={measured ? 'true' : 'false'}
      data-fits={result.fits ? 'true' : 'false'}
      data-collapsed-ids={result.collapsed.join(',')}
      ref={rowRef}
      style={{ gap: ROW_GAP }}
    >
      {groups.map((g, i) => (
        <React.Fragment key={g.id}>
          {dividers && i > 0 && (
            <span className={`rbn-sec-rule${g.tail ? ' rbn-sec-rule-tail' : ''}`} aria-hidden="true" />
          )}
          <div className={`rbn-slot${g.tail ? ' rbn-slot-tail' : ''}`}>
            <RibbonGroup
              group={g}
              collapsed={collapsedSet.has(g.id)}
              state={state}
              handlers={handlers}
              onOpenDrawer={onOpenDrawer}
              disabled={disabledGroupIds.has(g.id)}
            />
          </div>
        </React.Fragment>
      ))}

      {/* measure layer — never visible, never interactive */}
      <div className="rbn-measure" ref={measureRef} aria-hidden="true">
        {groups.map((g) => (
          <span key={g.id} data-measure-id={g.id} className="rbn-measure-item">
            <RibbonGroup group={g} collapsed={false} state={state} handlers={{}} onOpenDrawer={undefined} />
          </span>
        ))}
        {groups.filter((g) => !g.neverCollapse).map((g) => (
          <span key={`chip:${g.id}`} data-measure-id={`chip:${g.id}`} className="rbn-measure-item">
            {/* static chip lookalike — same classes, no menu logic */}
            <span className="rbn-chip">
              <span className="rbn-chip-label">{g.label}</span>
              <span className="rbn-ellipsis">…</span>
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
